import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { getJson } from "@/lib/http";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    appendTaskDefinitionIds,
    parseTaskDefinitionIds,
} from "@/lib/trackerTaskDefinitionFilter";

interface TaskDefinitionOption {
    id: number;
    name: string;
    label: string;
}

interface TaskDefinitionsResponse {
    cycleId: number;
    definitions: TaskDefinitionOption[];
}

interface CockpitTaskDefinitionFilterProps {
    cycleId: string | null;
    searchString: string;
    onSelectionChange: (nextSearch: string) => void;
}

function sameIdSet(a: number[], b: number[]): boolean {
    if (a.length !== b.length) {
        return false;
    }
    const sortedA = [...a].sort((left, right) => left - right);
    const sortedB = [...b].sort((left, right) => left - right);
    return sortedA.every((id, index) => id === sortedB[index]);
}

export function CockpitTaskDefinitionFilter({
    cycleId,
    searchString,
    onSelectionChange,
}: CockpitTaskDefinitionFilterProps) {
    const [open, setOpen] = useState(false);
    const [draftIds, setDraftIds] = useState<number[]>([]);

    const { data, isLoading } = useQuery<TaskDefinitionsResponse | null>({
        queryKey: ["/api/analytics/tracker/task-definitions", cycleId],
        queryFn: async () => {
            if (!cycleId) return null;
            return await getJson<TaskDefinitionsResponse>(
                `/api/analytics/tracker/task-definitions/${cycleId}`,
            );
        },
        enabled: !!cycleId,
        staleTime: 5 * 60 * 1000,
    });

    const definitions = data?.definitions ?? [];
    const allIds = useMemo(
        () => definitions.map((definition) => definition.id),
        [definitions],
    );

    const committedIds = useMemo(() => {
        const params = new URLSearchParams(searchString);
        if (!params.has("taskDefinitionIds")) {
            return allIds;
        }
        const parsed = parseTaskDefinitionIds(params.get("taskDefinitionIds"));
        if (parsed === null) {
            return allIds;
        }
        if (parsed.length === 0) {
            return [];
        }
        const allowed = new Set(allIds);
        return parsed.filter((id) => allowed.has(id));
    }, [searchString, allIds]);

    useEffect(() => {
        if (open) {
            setDraftIds(committedIds);
        }
    }, [open, committedIds]);

    const triggerLabel = useMemo(() => {
        if (isLoading || definitions.length === 0) {
            return "All operator tasks";
        }
        if (committedIds.length === 0) {
            return "No operator tasks";
        }
        if (committedIds.length === definitions.length) {
            return "All operator tasks";
        }
        const labels = definitions
            .filter((definition) => committedIds.includes(definition.id))
            .map((definition) => definition.label);
        if (labels.length <= 2) {
            return labels.join(", ");
        }
        return `${labels.length} operator tasks`;
    }, [committedIds, definitions, isLoading]);

    const commitSelection = (nextSelected: number[]) => {
        const params = new URLSearchParams(searchString);
        appendTaskDefinitionIds(params, nextSelected, allIds);
        onSelectionChange(params.toString());
    };

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen && open && !sameIdSet(draftIds, committedIds)) {
            commitSelection(draftIds);
        }
        setOpen(nextOpen);
    };

    const toggleDraftId = (definitionId: number, checked: boolean) => {
        setDraftIds((current) => {
            const next = new Set(current);
            if (checked) {
                next.add(definitionId);
            } else {
                next.delete(definitionId);
            }
            return Array.from(next);
        });
    };

    const allDraftSelected = draftIds.length === definitions.length && definitions.length > 0;

    if (!cycleId || definitions.length === 0) {
        return null;
    }

    return (
        <div className="min-w-[180px]">
            <p
                className="text-xs text-muted-foreground mb-1"
                title="Operator complete is for the selected operator tasks only."
            >
                Operator Tasks
            </p>
            <Popover open={open} onOpenChange={handleOpenChange}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        className={cn(
                            "flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 [&>span]:line-clamp-1",
                        )}
                    >
                        <span className="truncate">{triggerLabel}</span>
                        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                    </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-64 p-3">
                    <div className="flex items-center justify-between gap-2 pb-2 border-b mb-2">
                        <span className="text-sm font-medium">Operator tasks</span>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => setDraftIds(allDraftSelected ? [] : [...allIds])}
                        >
                            {allDraftSelected ? "Clear all" : "Select all"}
                        </Button>
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                        {definitions.map((definition) => {
                            const checked = draftIds.includes(definition.id);
                            return (
                                <label
                                    key={definition.id}
                                    htmlFor={`cockpit-operator-task-${definition.id}`}
                                    className="flex items-center gap-2 rounded-md px-1 py-1.5 text-sm cursor-pointer hover:bg-muted/60"
                                >
                                    <Checkbox
                                        id={`cockpit-operator-task-${definition.id}`}
                                        checked={checked}
                                        onCheckedChange={(value) => toggleDraftId(definition.id, value === true)}
                                    />
                                    <span>{definition.label}</span>
                                </label>
                            );
                        })}
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
}
